import type { Issue, Project } from "./types";
import { isTaskReportChannel, recordedSlackChannel } from "./taskReportOrigin";
import { NO_FILTER_VALUE } from "./taskFilters";
import { projectHumanMarkdown } from "./humanMarkdown";
import { humanOwnerId } from "./taskOwnership";
import { getExplicitFocusDependencies } from "./personalFocusEvidence";

export type FocusTask = Pick<Issue,
  "id" | "identifier" | "workspaceId" | "teamId" | "title" | "description" |
  "status" | "priority" | "assigneeId" | "ownerId" | "executorAgentId" |
  "projectId" | "labelIds" | "dueDate" | "createdAt" | "updatedAt" |
  "reportChannel" | "reportContext" | "reportUrl"
>;

export type FocusPreference = { issueId: string; confirmedAt: number };
export type FocusPeriod = 7 | 30 | 90 | "all";
export type FocusProject = Pick<Project, "id" | "workspaceId" | "name">;
export type FocusLabel = { id: string; name: string; teamId: string; teamName: string; workspaceId: string };
export type FocusFilters = { query: string; projectIds: string[]; labelIds: string[]; origins: string[]; slackChannels: string[] };
export type FocusFilterFacet = Exclude<keyof FocusFilters, "query">;
export type FocusFilterOption = { value: string; label: string };
export type FocusFilterOptions = Record<FocusFilterFacet, FocusFilterOption[]>;
export const emptyFocusFilters = (): FocusFilters => ({ query: "", projectIds: [], labelIds: [], origins: [], slackChannels: [] });
export const normalizeFocusText = (value: string) => value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\s+/g, " ").trim();
export function normalizeFocusFilters(filters: Partial<FocusFilters>): FocusFilters {
  const list = (values: unknown) => Array.isArray(values) ? [...new Set(values.filter((value): value is string =>
    typeof value === "string" && value.length > 0 && value.length <= 220 && !/[\u0000-\u001f\u007f]/.test(value)))].sort() : [];
  return {
    query: typeof filters.query === "string" ? filters.query.slice(0, 500) : "",
    projectIds: list(filters.projectIds), labelIds: list(filters.labelIds),
    origins: list(filters.origins).filter((value) => value === NO_FILTER_VALUE || isTaskReportChannel(value)),
    slackChannels: list(filters.slackChannels).filter((value) => value === NO_FILTER_VALUE || /^[a-z0-9][a-z0-9-]*\.slack\.com:C[A-Z0-9]{8,127}$/.test(value)),
  };
}
export const focusFiltersKey = (filters: FocusFilters) => JSON.stringify({ ...normalizeFocusFilters(filters), query: normalizeFocusText(filters.query) });
export const hasFocusFilters = (filters: FocusFilters) => !!normalizeFocusText(filters.query) || [filters.projectIds, filters.labelIds, filters.origins, filters.slackChannels].some((values) => values.length > 0);
const ORIGIN_NAMES: Record<string, string> = { pauta: "Pauta", slack: "Slack", codex: "Codex", claude_code: "Claude Code", github: "GitHub", api: "API", other: "Outra origem", [NO_FILTER_VALUE]: "Origem não registrada" };

/** Options come from the complete personal queue, before any view filter. */
export function personalFocusFilterOptions(tasks: readonly FocusTask[], projects: readonly FocusProject[], labels: readonly FocusLabel[], workspaceId: string): FocusFilterOptions {
  const own = tasks.filter((task) => task.workspaceId === workspaceId);
  const projectNames = new Map(projects.filter((project) => project.workspaceId === workspaceId).map((project) => [project.id, project.name]));
  const labelNames = new Map(labels.filter((label) => label.workspaceId === workspaceId).map((label) => [label.id, `${label.name} · ${label.teamName}`]));
  const values = (items: string[]) => [...new Set(items)].sort();
  const projectIds = values(own.map((task) => task.projectId ?? NO_FILTER_VALUE));
  const labelIds = values(own.flatMap((task) => task.labelIds.length ? task.labelIds : [NO_FILTER_VALUE]));
  const origins = values(own.map((task) => isTaskReportChannel(task.reportChannel) ? task.reportChannel : NO_FILTER_VALUE));
  const channels = values(own.filter((task) => task.reportChannel === "slack").map((task) => recordedSlackChannel(task) ?? NO_FILTER_VALUE));
  let channelNumber = 0;
  return {
    projectIds: projectIds.map((value) => ({ value, label: value === NO_FILTER_VALUE ? "Sem projeto" : projectNames.get(value) ?? "Projeto registrado sem nome disponível" })),
    labelIds: labelIds.map((value) => ({ value, label: value === NO_FILTER_VALUE ? "Sem tema registrado" : labelNames.get(value) ?? "Tema registrado sem nome disponível" })),
    origins: origins.map((value) => ({ value, label: ORIGIN_NAMES[value] })),
    slackChannels: channels.map((value) => ({ value, label: value === NO_FILTER_VALUE ? "Canal Slack não identificado" : `Canal Slack registrado ${++channelNumber}` })),
  };
}
export function taskMatchesFocusFilters(task: FocusTask, filters: FocusFilters): boolean {
  const query = normalizeFocusText(filters.query);
  // Search only visible task content. Comments (including malformed/truncated
  // internal blocks) are conservatively omitted, never used as source evidence.
  if (query) {
    const humanDescription = projectHumanMarkdown(task.description).body.replace(/<!--[\s\S]*?(?:-->|$)/g, " ");
    if (!normalizeFocusText(`${task.title} ${humanDescription}`).includes(query)) return false;
  }
  const contains = (selected: string[], value: string) => selected.length === 0 || selected.includes(value);
  if (!contains(filters.projectIds, task.projectId ?? NO_FILTER_VALUE)) return false;
  if (filters.labelIds.length && !filters.labelIds.some((id) => id === NO_FILTER_VALUE ? task.labelIds.length === 0 : task.labelIds.includes(id))) return false;
  if (!contains(filters.origins, isTaskReportChannel(task.reportChannel) ? task.reportChannel : NO_FILTER_VALUE)) return false;
  return filters.slackChannels.length === 0 || (task.reportChannel === "slack" && contains(filters.slackChannels, recordedSlackChannel(task) ?? NO_FILTER_VALUE));
}
export type PersonalFocusModel = {
  primary: FocusTask | null;
  mode: "confirmed" | "suggested" | "empty";
  reason: string;
  waiting: boolean;
  nextExecutable: FocusTask | null;
  nextByProject: { projectId: string | null; name: string; tasks: FocusTask[] }[];
  previousFocusUnavailable: boolean;
  period: FocusPeriod;
  primaryOutsidePeriod: boolean;
  primaryOutsideFilters?: boolean;
  filters?: FocusFilters;
  counts?: { total: number; matching: number };
};

const OPEN_STATUSES = new Set<FocusTask["status"]>(["in_progress", "todo", "backlog", "in_review"]);
const AUTOMATIC_STATUSES = new Set<FocusTask["status"]>(["in_progress", "todo", "in_review"]);
const STATUS_RANK = { in_progress: 0, todo: 1, backlog: 2, in_review: 3, done: 4, canceled: 5 };
const PRIORITY_NAMES = { 0: "sem prioridade", 1: "urgente", 2: "alta", 3: "média", 4: "baixa" };
const priorityRank = (task: FocusTask) => task.priority === 0 ? 5 : task.priority;
const dateRank = (value: number | null) => value != null && Number.isFinite(value) ? value : Infinity;
const validTimestamp = (value: number) => Number.isFinite(value) && Number.isFinite(new Date(value).getTime());
const creationRank = (value: number) => validTimestamp(value) ? value : -Infinity;
const DAY_MS = 24 * 60 * 60 * 1000;
const compareIds = (a: string, b: string) => a < b ? -1 : a > b ? 1 : 0;

/** Recorded priority, deadline and status precede creation recency. Updates never change rank. */
export function comparePersonalFocusTasks(a: FocusTask, b: FocusTask): number {
  return priorityRank(a) - priorityRank(b) ||
    dateRank(a.dueDate) - dateRank(b.dueDate) ||
    STATUS_RANK[a.status] - STATUS_RANK[b.status] ||
    creationRank(b.createdAt) - creationRank(a.createdAt) ||
    compareIds(a.id, b.id);
}

function suggestedReason(primary: FocusTask, second: FocusTask | undefined, period: FocusPeriod): string {
  if (!second) return `É o único ticket fora do backlog${period === "all" ? "" : " no período selecionado"} sob sua responsabilidade neste workspace.`;
  if (priorityRank(primary) !== priorityRank(second)) {
    return `Tem prioridade registrada ${PRIORITY_NAMES[primary.priority]}, acima das demais tarefas fora do backlog.`;
  }
  if (dateRank(primary.dueDate) !== dateRank(second.dueDate)) {
    return "Entre tarefas fora do backlog, com a mesma prioridade, tem o prazo registrado mais cedo.";
  }
  if (STATUS_RANK[primary.status] !== STATUS_RANK[second.status]) {
    if (primary.status === "in_progress") return "Com a mesma prioridade e prazo, está marcado como Em andamento.";
    if (primary.status === "todo") return "Com a mesma prioridade e prazo, está em A fazer, antes da revisão.";
    return "Com a mesma prioridade e prazo, aparece antes dos tickets em revisão.";
  }
  if (creationRank(primary.createdAt) !== creationRank(second.createdAt)) {
    return "Com a mesma prioridade, prazo e andamento, é o ticket criado mais recentemente fora do backlog.";
  }
  return "Tem a mesma prioridade, prazo e andamento das demais tarefas fora do backlog; a ordem permanece estável.";
}

/** A personal projection over already-authorized rows, never a grant of access. */
export function selectPersonalFocus({
  workspaceId, userId, issues, projects, preference, dependencyTasks = issues, period = 30, now = Date.now(), filters: rawFilters = emptyFocusFilters(),
}: {
  workspaceId: string | null;
  userId: string | null;
  issues: readonly FocusTask[];
  projects: readonly FocusProject[];
  preference: FocusPreference | null;
  dependencyTasks?: readonly FocusTask[];
  period?: FocusPeriod;
  now?: number;
  filters?: FocusFilters;
}): PersonalFocusModel {
  const eligible = workspaceId && userId
    ? issues.filter((task) => task.workspaceId === workspaceId &&
      humanOwnerId(task) === userId && OPEN_STATUSES.has(task.status))
    : [];
  const inPeriod = (task: FocusTask) => period === "all" ||
    (validTimestamp(now) && validTimestamp(task.createdAt) && task.createdAt >= now - period * DAY_MS && task.createdAt <= now);
  const filters = normalizeFocusFilters(rawFilters);
  const ranked = eligible.filter((task) => inPeriod(task) && taskMatchesFocusFilters(task, filters)).sort(comparePersonalFocusTasks);
  const automatic = ranked.filter((task) => AUTOMATIC_STATUSES.has(task.status));
  // The persisted personal choice is resolved from the complete authorized
  // inventory; a view filter must never silently replace that choice.
  const confirmed = preference ? eligible.find((task) => task.id === preference.issueId) : undefined;
  const primary = confirmed ?? automatic[0] ?? null;
  const previousFocusUnavailable = !!workspaceId && !!userId && !!preference && !confirmed;
  const visibleDependencies = dependencyTasks.filter((task) => task.workspaceId === workspaceId);
  const waitingReason = (task: FocusTask): string | null => {
    if (task.status === "in_review") return "Precisa de revisão/decisão; abra o ticket para conferir quem deve revisar.";
    const dependencies = getExplicitFocusDependencies(task, [task, ...visibleDependencies.filter((candidate) => candidate.id !== task.id)]);
    if (dependencies.some((dependency) => dependency.state === "open")) return "Dependência registrada pendente.";
    if (dependencies.some((dependency) => dependency.state === "unverified")) return "Dependência registrada sem estado verificável.";
    return null;
  };
  const waitingMessage = primary ? waitingReason(primary) : null;
  const waiting = waitingMessage !== null;
  const remaining = ranked.filter((task) => task.id !== primary?.id);
  const availableProjects = new Map(projects
    .filter((project) => project.workspaceId === workspaceId)
    .map((project) => [project.id, project]));
  const groups = new Map<string | null, PersonalFocusModel["nextByProject"][number]>();
  for (const task of remaining) {
    const project = task.projectId ? availableProjects.get(task.projectId) : undefined;
    const projectId = task.projectId;
    let group = groups.get(projectId);
    if (!group) {
      group = { projectId, name: project?.name ?? (projectId ? "Projeto registrado sem nome disponível" : "Sem projeto"), tasks: [] };
      groups.set(projectId, group);
    }
    group.tasks.push(task);
  }
  return {
    primary,
    mode: confirmed ? "confirmed" : primary ? "suggested" : "empty",
    reason: (confirmed ? "Você confirmou este foco. Ele permanece até você escolher outro."
      : primary ? `${hasFocusFilters(filters) ? "Nos filtros selecionados: " : ""}${suggestedReason(primary, automatic[1], period)}`
        : ranked.length ? `Nenhuma tarefa pronta para execução ou revisão${period === "all" ? "" : " neste período"}. Suas tarefas no backlog continuam disponíveis para escolha manual.`
        : eligible.length && hasFocusFilters(filters) ? "Nenhuma tarefa corresponde à combinação de filtros. Ajuste texto, projeto, tema, origem, canal ou período para ver as demais tarefas."
        : eligible.length && period !== "all" ? `Nenhuma tarefa criada nos últimos ${period} dias está disponível neste filtro. Amplie o período ou escolha Todos para ver as demais tarefas.`
        : "Nenhum ticket aberto está atribuído a você neste workspace.") + (waitingMessage ? ` ${waitingMessage}` : ""),
    waiting,
    nextExecutable: waiting ? remaining.find((task) => AUTOMATIC_STATUSES.has(task.status) && waitingReason(task) === null) ?? null : null,
    nextByProject: [...groups.values()],
    previousFocusUnavailable,
    period,
    primaryOutsidePeriod: !!confirmed && !inPeriod(confirmed),
    primaryOutsideFilters: !!confirmed && !taskMatchesFocusFilters(confirmed, filters),
    filters, counts: { total: eligible.length, matching: ranked.length },
  };
}
